import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { GoalDocument, Goal, GoalStatus, BehavioralRatings, Signatures } from '../types';

// ===========================================
// INPUT VALIDATION & SANITIZATION UTILITIES
// ===========================================

const MAX_GOAL_TITLE_LENGTH = 500;
const MAX_GOAL_DESCRIPTION_LENGTH = 5000;
const MAX_NAME_LENGTH = 200;
const MAX_EMAIL_LENGTH = 254;
const VALID_RATINGS = ['A', 'B', 'NI', null] as const;
const VALID_STATUSES: GoalStatus[] = ['draft', 'submitted', 'initial_review', 'approved', 'employee_final_review', 'final_review', 'completed'];

/**
 * Gets the first day of the quarter following the given quarter/year
 */
export const getQuarterEndDate = (quarter: number, year: number): Date => {
  // Q1 ends Apr 1, Q2 ends Jul 1, Q3 ends Oct 1, Q4 ends Jan 1 next year
  const nextQuarterMonth = quarter * 3; // 3, 6, 9, 12
  if (nextQuarterMonth === 12) {
    return new Date(year + 1, 0, 1); // Jan 1 next year
  }
  return new Date(year, nextQuarterMonth, 1);
};

/**
 * Checks if employee final review is unlocked for a given quarter
 * Employee can only start final review on or after the first day of the next quarter
 */
/**
 * Checks if employee final review is unlocked for a given quarter
 * Employee can only start final review on or after the first day of the next quarter
 * 
 * @param isDeveloper - If true, bypasses the date lock (for testing/demo)
 */
export const isEmployeeFinalReviewUnlocked = (quarter: number, year: number, isDeveloper: boolean = false): boolean => {
  // Developer override for testing
  if (isDeveloper) {
    return true;
  }
  
  const unlockDate = getQuarterEndDate(quarter, year);
  const now = new Date();
  return now >= unlockDate;
};

/**
 * Gets display text for when final review unlocks
 */
export const getFinalReviewUnlockDate = (quarter: number, year: number): string => {
  const unlockDate = getQuarterEndDate(quarter, year);
  return unlockDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

/**
 * Sanitizes a string by trimming whitespace and removing potentially dangerous characters.
 * Does NOT remove HTML tags as React handles XSS prevention, but limits length.
 */
const sanitizeString = (input: string, maxLength: number): string => {
  if (typeof input !== 'string') return '';
  return input.trim().slice(0, maxLength);
};

/**
 * Validates email format
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return typeof email === 'string' && 
         email.length <= MAX_EMAIL_LENGTH && 
         emailRegex.test(email);
};

/**
 * Validates a goal object
 */
const validateGoal = (goal: Goal): Goal => {
  if (!goal || typeof goal !== 'object') {
    throw new Error('Invalid goal object');
  }
  
  return {
    number: typeof goal.number === 'number' ? goal.number : 0,
    title: sanitizeString(goal.title || '', MAX_GOAL_TITLE_LENGTH),
    description: sanitizeString(goal.description || '', MAX_GOAL_DESCRIPTION_LENGTH),
    type: ['normal', 'stretch', 'hard'].includes(goal.type) ? goal.type : 'normal',
    initialApproval: typeof goal.initialApproval === 'boolean' ? goal.initialApproval : null,
    employeeRating: VALID_RATINGS.includes(goal.employeeRating as any) ? goal.employeeRating : null,
    managerRating: VALID_RATINGS.includes(goal.managerRating as any) ? goal.managerRating : null,
  };
};

/**
 * Validates an array of goals
 */
const validateGoals = (goals: Goal[]): Goal[] => {
  if (!Array.isArray(goals) || goals.length !== 5) {
    throw new Error('Exactly 5 goals are required');
  }
  return goals.map(validateGoal);
};

/**
 * Validates quarter (1-4)
 */
const validateQuarter = (quarter: number): number => {
  if (typeof quarter !== 'number' || quarter < 1 || quarter > 4) {
    throw new Error('Quarter must be between 1 and 4');
  }
  return quarter;
};

/**
 * Validates year (reasonable range)
 */
const validateYear = (year: number): number => {
  const currentYear = new Date().getFullYear();
  if (typeof year !== 'number' || year < currentYear - 5 || year > currentYear + 1) {
    throw new Error('Invalid year');
  }
  return year;
};

// ===========================================
// SERVICE FUNCTIONS
// ===========================================

export const getCurrentQuarter = (): { quarter: number; year: number } => {
  const now = new Date();
  const month = now.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  return { quarter, year: now.getFullYear() };
};

/**
 * Gets quarters available for goal creation
 * - Current quarter is always available
 * - Next quarter is available starting on the 1st of the last month of current quarter
 *   (March 1 for Q2, June 1 for Q3, September 1 for Q4, December 1 for Q1 next year)
 */
export const getAvailableQuartersForCreation = (): { quarter: number; year: number }[] => {
  const now = new Date();
  const month = now.getMonth(); // 0-11
  const year = now.getFullYear();
  const currentQuarter = Math.floor(month / 3) + 1;
  
  const quarters: { quarter: number; year: number }[] = [
    { quarter: currentQuarter, year }
  ];
  
  // Check if we're in the last month of the quarter (March, June, September, December)
  // Month indices: Feb=1, Mar=2, May=4, Jun=5, Aug=7, Sep=8, Nov=10, Dec=11
  const lastMonthOfQuarter = currentQuarter * 3 - 1; // Q1->2 (Mar), Q2->5 (Jun), Q3->8 (Sep), Q4->11 (Dec)
  
  if (month === lastMonthOfQuarter) {
    // Add next quarter
    if (currentQuarter === 4) {
      quarters.push({ quarter: 1, year: year + 1 });
    } else {
      quarters.push({ quarter: currentQuarter + 1, year });
    }
  }
  
  return quarters;
};

/**
 * Checks if a specific quarter is available for goal creation
 */
export const canCreateGoalsForQuarter = (quarter: number, year: number): boolean => {
  const available = getAvailableQuartersForCreation();
  return available.some(q => q.quarter === quarter && q.year === year);
};

export const createGoalDocument = async (
  userId: string,
  userEmail: string,
  userName: string,
  userTitle: string,
  goals: Goal[],
  quarter: number,
  year: number,
  managerEmail: string,
  status: 'draft' | 'submitted' = 'submitted'
): Promise<string> => {
  // Validate all inputs
  if (!userId || typeof userId !== 'string') {
    throw new Error('Invalid user ID');
  }
  if (!isValidEmail(userEmail)) {
    throw new Error('Invalid user email');
  }
  if (!isValidEmail(managerEmail)) {
    throw new Error('Invalid manager email');
  }
  
  const sanitizedUserName = sanitizeString(userName, MAX_NAME_LENGTH);
  const sanitizedUserTitle = sanitizeString(userTitle, MAX_NAME_LENGTH);
  const validatedGoals = validateGoals(goals);
  const validatedQuarter = validateQuarter(quarter);
  const validatedYear = validateYear(year);

  const goalDoc: Omit<GoalDocument, 'id'> = {
    userId,
    userEmail: userEmail.toLowerCase().trim(),
    userName: sanitizedUserName,
    userTitle: sanitizedUserTitle,
    quarter: validatedQuarter,
    year: validatedYear,
    goals: validatedGoals,
    status,
    salesGoal: { percentage: null, amount: null },
    performanceGoal: { percentage: null, amount: null },
    behavioralRatings: {
      respectForOthers: null,
      integrity: null,
      teamwork: null,
      biasForAction: null,
      carefulCommunication: null,
      growthMindset: null,
      leadByExample: null,
    },
    signatures: {
      employeeSet: status === 'submitted' ? {
        name: sanitizedUserName,
        date: Timestamp.now(),
      } : null,
      managerSet: null,
      employeeReview: null,
      managerReview: null,
    },
    managerEmail: managerEmail.toLowerCase().trim(),
    createdAt: serverTimestamp() as Timestamp,
    updatedAt: serverTimestamp() as Timestamp,
  };

  const docRef = await addDoc(collection(db, 'goals'), goalDoc);
  return docRef.id;
};

/**
 * Update a draft goal document
 * Can update goals array and optionally submit (change status to submitted)
 */
export const updateGoalDraft = async (
  goalId: string,
  goals: Goal[],
  userName: string,
  submit: boolean = false
): Promise<void> => {
  const validatedGoals = validateGoals(goals);
  const sanitizedUserName = sanitizeString(userName, MAX_NAME_LENGTH);
  
  const updateData: Record<string, any> = {
    goals: validatedGoals,
    updatedAt: serverTimestamp(),
  };
  
  if (submit) {
    updateData.status = 'submitted';
    updateData.signatures = {
      employeeSet: {
        name: sanitizedUserName,
        date: Timestamp.now(),
      },
      managerSet: null,
      employeeReview: null,
      managerReview: null,
    };
  }
  
  const goalRef = doc(db, 'goals', goalId);
  await updateDoc(goalRef, updateData);
};

/**
 * Save edits to an already-submitted goal (when allowEdit is true)
 * Turns off allowEdit after saving
 */
export const saveGoalEdits = async (
  goalId: string,
  goals: Goal[]
): Promise<void> => {
  const validatedGoals = validateGoals(goals);
  
  const goalRef = doc(db, 'goals', goalId);
  await updateDoc(goalRef, {
    goals: validatedGoals,
    allowEdit: false,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Toggle allowEdit flag on a goal (manager only)
 * When true, employee can edit their submitted goals
 */
export const toggleAllowEdit = async (
  goalId: string,
  allowEdit: boolean
): Promise<void> => {
  const goalRef = doc(db, 'goals', goalId);
  await updateDoc(goalRef, {
    allowEdit,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Set selfReviewOpenDate on a goal (manager/super admin)
 * When set to a date in the past or today, employee can do self-review
 * Pass null to clear the override
 */
export const setSelfReviewOpenDate = async (
  goalId: string,
  openDate: Date | null
): Promise<void> => {
  const goalRef = doc(db, 'goals', goalId);
  await updateDoc(goalRef, {
    selfReviewOpenDate: openDate ? Timestamp.fromDate(openDate) : null,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Check if self-review is open via the selfReviewOpenDate override (per-goal)
 */
export const isSelfReviewOpenByDate = (goal: GoalDocument): boolean => {
  if (!goal.selfReviewOpenDate) return false;
  const openDate = goal.selfReviewOpenDate.toDate();
  const now = new Date();
  return openDate <= now;
};

/**
 * Get global self-review settings
 */
export const getGlobalSelfReviewSettings = async (): Promise<{
  openDate: Date | null;
  managerOpenDate: Date | null;
  quarter: number | null;
  year: number | null;
}> => {
  try {
    const settingsRef = doc(db, 'settings', 'global');
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      return { openDate: null, managerOpenDate: null, quarter: null, year: null };
    }
    
    const data = settingsDoc.data();
    return {
      openDate: data.selfReviewOpenDate?.toDate() || null,
      managerOpenDate: data.managerReviewOpenDate?.toDate() || null,
      quarter: data.selfReviewQuarter || null,
      year: data.selfReviewYear || null,
    };
  } catch (error) {
    console.error('Error getting global settings:', error);
    return { openDate: null, managerOpenDate: null, quarter: null, year: null };
  }
};

/**
 * Set global self-review open date (super admin/developer only)
 * This opens self-review for ALL goals of a specific quarter/year
 */
export const setGlobalSelfReviewDate = async (
  openDate: Date | null,
  quarter: number,
  year: number
): Promise<void> => {
  const settingsRef = doc(db, 'settings', 'global');
  await setDoc(settingsRef, {
    selfReviewOpenDate: openDate ? Timestamp.fromDate(openDate) : null,
    selfReviewQuarter: quarter,
    selfReviewYear: year,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

/**
 * Check if self-review is globally open for a specific quarter/year
 */
export const isGlobalSelfReviewOpen = async (quarter: number, year: number): Promise<boolean> => {
  const settings = await getGlobalSelfReviewSettings();
  
  if (!settings.openDate || settings.quarter !== quarter || settings.year !== year) {
    return false;
  }
  
  const now = new Date();
  return settings.openDate <= now;
};

/**
 * Check if manager review is globally open for a specific quarter/year
 */
export const isGlobalManagerReviewOpen = async (quarter: number, year: number): Promise<boolean> => {
  const settings = await getGlobalSelfReviewSettings();
  
  if (!settings.managerOpenDate || settings.quarter !== quarter || settings.year !== year) {
    return false;
  }
  
  const now = new Date();
  return settings.managerOpenDate <= now;
};

export const getGoalById = async (goalId: string): Promise<GoalDocument | null> => {
  try {
    const docRef = doc(db, 'goals', goalId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as GoalDocument;
    }
    return null;
  } catch (error) {
    console.error('Error getting goal:', error);
    return null;
  }
};

export const getUserGoals = async (userId: string): Promise<GoalDocument[]> => {
  try {
    const q = query(
      collection(db, 'goals'),
      where('userId', '==', userId),
      orderBy('year', 'desc'),
      orderBy('quarter', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as GoalDocument));
  } catch (error) {
    console.error('Error getting user goals:', error);
    return [];
  }
};

export const getUserCurrentGoals = async (
  userId: string,
  quarter: number,
  year: number
): Promise<GoalDocument | null> => {
  try {
    const q = query(
      collection(db, 'goals'),
      where('userId', '==', userId),
      where('quarter', '==', quarter),
      where('year', '==', year)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as GoalDocument;
    }
    return null;
  } catch (error) {
    console.error('Error getting current goals:', error);
    return null;
  }
};

export const getTeamGoals = async (managerEmail: string): Promise<GoalDocument[]> => {
  try {
    const q = query(
      collection(db, 'goals'),
      where('managerEmail', '==', managerEmail),
      orderBy('year', 'desc'),
      orderBy('quarter', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as GoalDocument));
  } catch (error) {
    console.error('Error getting team goals:', error);
    return [];
  }
};

/**
 * Gets goals for all team members recursively (direct reports and their reports)
 * Uses batched queries to handle Firestore's 30-item 'in' clause limit
 */
export const getTeamGoalsRecursive = async (managerEmail: string): Promise<GoalDocument[]> => {
  try {
    // Import here to avoid circular dependency
    const { getAllTeamEmails } = await import('./userService');
    const teamEmails = await getAllTeamEmails(managerEmail);
    
    if (teamEmails.length === 0) {
      return [];
    }

    // Firestore 'in' queries are limited to 30 items, so batch them
    const batchSize = 30;
    const allGoals: GoalDocument[] = [];
    
    for (let i = 0; i < teamEmails.length; i += batchSize) {
      const batch = teamEmails.slice(i, i + batchSize);
      const q = query(
        collection(db, 'goals'),
        where('userEmail', 'in', batch),
        orderBy('year', 'desc'),
        orderBy('quarter', 'desc')
      );
      const snapshot = await getDocs(q);
      const batchGoals = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as GoalDocument));
      allGoals.push(...batchGoals);
    }

    // Sort combined results
    return allGoals.sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      return b.quarter - a.quarter;
    });
  } catch (error) {
    console.error('Error getting recursive team goals:', error);
    return [];
  }
};

export const getTeamGoalsByStatus = async (
  managerEmail: string,
  statuses: GoalStatus[],
  quarter?: number,
  year?: number
): Promise<GoalDocument[]> => {
  try {
    const q = query(
      collection(db, 'goals'),
      where('managerEmail', '==', managerEmail),
      where('status', 'in', statuses)
    );
    
    const snapshot = await getDocs(q);
    let results = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as GoalDocument));
    
    if (quarter !== undefined && year !== undefined) {
      results = results.filter(g => g.quarter === quarter && g.year === year);
    }
    
    return results;
  } catch (error) {
    console.error('Error getting team goals by status:', error);
    return [];
  }
};

/**
 * Gets goals by status for all team members recursively
 */
export const getTeamGoalsByStatusRecursive = async (
  managerEmail: string,
  statuses: GoalStatus[],
  quarter?: number,
  year?: number
): Promise<GoalDocument[]> => {
  try {
    const { getAllTeamEmails } = await import('./userService');
    const teamEmails = await getAllTeamEmails(managerEmail);
    
    if (teamEmails.length === 0) {
      return [];
    }

    const batchSize = 30;
    const allGoals: GoalDocument[] = [];
    
    for (let i = 0; i < teamEmails.length; i += batchSize) {
      const batch = teamEmails.slice(i, i + batchSize);
      const q = query(
        collection(db, 'goals'),
        where('userEmail', 'in', batch),
        where('status', 'in', statuses)
      );
      const snapshot = await getDocs(q);
      const batchGoals = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as GoalDocument));
      allGoals.push(...batchGoals);
    }

    // Filter by quarter/year if specified
    let results = allGoals;
    if (quarter !== undefined && year !== undefined) {
      results = allGoals.filter(g => g.quarter === quarter && g.year === year);
    }

    return results;
  } catch (error) {
    console.error('Error getting recursive team goals by status:', error);
    return [];
  }
};

export const getAllGoals = async (): Promise<GoalDocument[]> => {
  try {
    const q = query(
      collection(db, 'goals'),
      orderBy('year', 'desc'),
      orderBy('quarter', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as GoalDocument));
  } catch (error) {
    console.error('Error getting all goals:', error);
    return [];
  }
};

export const updateGoalStatus = async (
  goalId: string,
  status: GoalStatus
): Promise<void> => {
  // Validate status
  if (!VALID_STATUSES.includes(status)) {
    throw new Error('Invalid goal status');
  }
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  
  await updateDoc(doc(db, 'goals', goalId), {
    status,
    updatedAt: serverTimestamp(),
  });
};

export const updateGoals = async (
  goalId: string,
  goals: Goal[]
): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  
  // Validate and sanitize goals
  const validatedGoals = validateGoals(goals);
  
  await updateDoc(doc(db, 'goals', goalId), {
    goals: validatedGoals,
    updatedAt: serverTimestamp(),
  });
};

// Validate goal index is within bounds
const validateGoalIndex = (goals: Goal[], index: number): void => {
  if (typeof index !== 'number' || index < 0 || index >= goals.length) {
    throw new Error(`Invalid goal index: ${index}. Must be between 0 and ${goals.length - 1}`);
  }
};

// Validate rating value
const validateRating = (rating: string): rating is 'A' | 'B' | 'NI' => {
  return ['A', 'B', 'NI'].includes(rating);
};

// Validate behavioral rating value
const validateBehavioralRating = (rating: string | null): rating is 'E' | 'A' | 'B' | null => {
  return rating === null || ['E', 'A', 'B'].includes(rating);
};

// Validate incentive values
const validateIncentive = (incentive: { percentage: number | null; amount: number | null }): void => {
  if (incentive.percentage !== null) {
    if (typeof incentive.percentage !== 'number' || incentive.percentage < 0 || incentive.percentage > 200) {
      throw new Error('Percentage must be between 0 and 200');
    }
  }
  if (incentive.amount !== null) {
    if (typeof incentive.amount !== 'number' || incentive.amount < 0 || incentive.amount > 1000000) {
      throw new Error('Amount must be between 0 and 1,000,000');
    }
  }
};

export const updateGoalInitialApproval = async (
  goalId: string,
  goalIndex: number,
  approved: boolean
): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  if (typeof approved !== 'boolean') {
    throw new Error('Approved must be a boolean');
  }
  
  const goalDoc = await getGoalById(goalId);
  if (!goalDoc) {
    throw new Error('Goal not found');
  }
  
  validateGoalIndex(goalDoc.goals, goalIndex);
  
  const updatedGoals = [...goalDoc.goals];
  updatedGoals[goalIndex].initialApproval = approved;
  await updateDoc(doc(db, 'goals', goalId), {
    goals: updatedGoals,
    updatedAt: serverTimestamp(),
  });
};

export const completeInitialReview = async (goalId: string, managerName: string): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  if (!managerName || typeof managerName !== 'string' || managerName.length > 200) {
    throw new Error('Invalid manager name');
  }
  
  const goalDoc = await getGoalById(goalId);
  if (!goalDoc) {
    throw new Error('Goal not found');
  }
  
  // Approve all goals
  const approvedGoals = goalDoc.goals.map(g => ({ ...g, initialApproval: true }));
  
  // Update with approved goals, status, and manager signature
  await updateDoc(doc(db, 'goals', goalId), {
    goals: approvedGoals,
    status: 'approved',
    signatures: {
      ...goalDoc.signatures,
      managerSet: {
        name: managerName.trim().slice(0, 200),
        date: Timestamp.now(),
      },
    },
    updatedAt: serverTimestamp(),
  });
};

export const updateGoalRating = async (
  goalId: string,
  goalIndex: number,
  ratingType: 'employee' | 'manager',
  rating: 'A' | 'B' | 'NI'
): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  if (!['employee', 'manager'].includes(ratingType)) {
    throw new Error('Invalid rating type');
  }
  if (!validateRating(rating)) {
    throw new Error('Invalid rating value. Must be A, B, or NI');
  }
  
  const goalDoc = await getGoalById(goalId);
  if (!goalDoc) {
    throw new Error('Goal not found');
  }
  
  validateGoalIndex(goalDoc.goals, goalIndex);
  
  const updatedGoals = [...goalDoc.goals];
  if (ratingType === 'employee') {
    updatedGoals[goalIndex].employeeRating = rating;
  } else {
    updatedGoals[goalIndex].managerRating = rating;
  }
  await updateDoc(doc(db, 'goals', goalId), {
    goals: updatedGoals,
    updatedAt: serverTimestamp(),
  });
};

export const updateBehavioralRatings = async (
  goalId: string,
  ratings: BehavioralRatings
): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  
  // Validate all behavioral ratings
  const ratingKeys: (keyof BehavioralRatings)[] = [
    'respectForOthers', 'integrity', 'teamwork', 'biasForAction',
    'carefulCommunication', 'growthMindset', 'leadByExample'
  ];
  
  for (const key of ratingKeys) {
    if (!validateBehavioralRating(ratings[key])) {
      throw new Error(`Invalid behavioral rating for ${key}. Must be E, A, B, or null`);
    }
  }
  
  await updateDoc(doc(db, 'goals', goalId), {
    behavioralRatings: ratings,
    updatedAt: serverTimestamp(),
  });
};

export const updateIncentiveGoals = async (
  goalId: string,
  salesGoal: { percentage: number | null; amount: number | null },
  performanceGoal: { percentage: number | null; amount: number | null }
): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  
  // Validate incentive values
  validateIncentive(salesGoal);
  validateIncentive(performanceGoal);
  
  await updateDoc(doc(db, 'goals', goalId), {
    salesGoal,
    performanceGoal,
    updatedAt: serverTimestamp(),
  });
};

export const addSignature = async (
  goalId: string,
  signatureType: keyof Signatures,
  name: string
): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  
  const validSignatureTypes: (keyof Signatures)[] = [
    'employeeSet', 'managerSet', 'employeeReview', 'managerReview'
  ];
  if (!validSignatureTypes.includes(signatureType)) {
    throw new Error('Invalid signature type');
  }
  
  if (!name || typeof name !== 'string' || name.length > 200) {
    throw new Error('Invalid signature name');
  }
  
  const goalDoc = await getGoalById(goalId);
  if (!goalDoc) {
    throw new Error('Goal not found');
  }
  
  const updatedSignatures = { ...goalDoc.signatures };
  updatedSignatures[signatureType] = {
    name: name.trim().slice(0, 200),
    date: Timestamp.now(),
  };
  await updateDoc(doc(db, 'goals', goalId), {
    signatures: updatedSignatures,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Employee submits their end-of-quarter self-review
 * This moves the goal from 'approved' to 'employee_final_review' then to 'final_review'
 * Can only be done after the quarter ends
 * 
 * @param isDeveloper - If true, bypasses the date lock (for testing/demo)
 */
export const submitEmployeeFinalReview = async (
  goalId: string,
  employeeName: string,
  isDeveloper: boolean = false
): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  if (!employeeName || typeof employeeName !== 'string' || employeeName.length > 200) {
    throw new Error('Invalid employee name');
  }
  
  const goalDoc = await getGoalById(goalId);
  if (!goalDoc) {
    throw new Error('Goal not found');
  }
  
  // Check if self-review is allowed:
  // 1. Date-based unlock (quarter has ended) OR
  // 2. selfReviewOpenDate is set and has passed OR
  // 3. Global self-review is open for this quarter OR
  // 4. User is a developer
  const dateUnlocked = isEmployeeFinalReviewUnlocked(goalDoc.quarter, goalDoc.year, isDeveloper);
  const overrideOpen = isSelfReviewOpenByDate(goalDoc);
  const globalOpen = await isGlobalSelfReviewOpen(goalDoc.quarter, goalDoc.year);
  const isOpen = dateUnlocked || overrideOpen || globalOpen;
  
  if (!isOpen) {
    const unlockDate = getFinalReviewUnlockDate(goalDoc.quarter, goalDoc.year);
    throw new Error(`Final review is not available until ${unlockDate}`);
  }
  
  // Verify all goals have employee ratings
  const missingRatings = goalDoc.goals.filter(g => !g.employeeRating);
  if (missingRatings.length > 0) {
    throw new Error('Please rate all goals before submitting');
  }
  
  // Add employee review signature and update status
  const updatedSignatures = { ...goalDoc.signatures };
  updatedSignatures.employeeReview = {
    name: employeeName.trim().slice(0, 200),
    date: Timestamp.now(),
  };
  
  await updateDoc(doc(db, 'goals', goalId), {
    status: 'final_review',
    signatures: updatedSignatures,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Manager completes the final review after employee has done theirs
 */
export const completeFinalReview = async (goalId: string, managerName: string): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  if (!managerName || typeof managerName !== 'string' || managerName.length > 200) {
    throw new Error('Invalid manager name');
  }
  
  const goalDoc = await getGoalById(goalId);
  if (!goalDoc) {
    throw new Error('Goal not found');
  }
  
  // Add manager review signature
  const updatedSignatures = { ...goalDoc.signatures };
  updatedSignatures.managerReview = {
    name: managerName.trim().slice(0, 200),
    date: Timestamp.now(),
  };
  
  await updateDoc(doc(db, 'goals', goalId), {
    status: 'completed',
    signatures: updatedSignatures,
    updatedAt: serverTimestamp(),
  });
};

export const deleteGoal = async (goalId: string): Promise<void> => {
  if (!goalId || typeof goalId !== 'string') {
    throw new Error('Invalid goal ID');
  }
  
  await deleteDoc(doc(db, 'goals', goalId));
};